import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { formatInr } from "./runPayrollUtils";
import { enrichEmployeesWithSalaryBreakdown } from "../../../utils/payrollSalaryBreakdownEnrichment";

const EMPLOYER_STATUTORY_RE =
  /\bpf\s*\(employer\)|esi\s*\(employer\)|esic\s*\(employer\)|^eps\b|edli|admin\s*charges?/i;

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

function sumField(rows, key) {
  return round2((rows || []).reduce((s, r) => s + (Number(r[key]) || 0), 0));
}

function isArrStatutory(name) {
  return /^arr\.?\s*(pf|esi)\b/i.test(String(name || "").trim());
}

function isLegacyLopReversal(name) {
  return /lop\s*reversal/i.test(String(name || ""));
}

function isNonPayrollBenefit(name, meta = {}) {
  const code = String(meta?.code || "").toUpperCase();
  const n = String(name || "").toLowerCase();
  if (code === "VPF" || n === "vpf" || n.includes("voluntary provident")) return false;
  if (
    meta?.nonPayroll === true ||
    String(meta?.componentNature || "").toLowerCase() === "nonpayroll"
  ) {
    if (/deduct/i.test(String(meta?.type || meta?.category || ""))) return false;
    return true;
  }
  if (code === "NPS_EMPLOYER" || code === "MEAL_VOUCHER" || code === "GRATUITY") return true;
  if (n.includes("nps (employer)") || n === "employer nps") return true;
  if (n.includes("meal voucher") || n.includes("food coupon")) return true;
  if (/gratuity/i.test(n) && !n.includes("employee")) return true;
  return false;
}

function isEmployeeDeduction(name, amount, meta = {}) {
  const n = String(name || "").toLowerCase();
  const type = String(meta.type || meta.category || "").toLowerCase();
  if (isArrStatutory(n)) return true;
  if (/deduct/i.test(type) || Number(amount) < 0) return true;
  return (
    /\bpf\s*\(\s*employee\s*\)/.test(n) ||
    /\besi\s*\(\s*employee\s*\)/.test(n) ||
    /professional\s*tax/.test(n) ||
    /\btds\b/.test(n) ||
    /income\s*tax/.test(n) ||
    /\bvpf\b/.test(n) ||
    n.includes("deduction")
  );
}

function isMonthlyVariableMeta(meta = {}) {
  if (meta?.isMonthlyVariable === true) return true;
  const method = String(meta.calculationMethod || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (method === "monthlyvariable" || method === "onetime") return true;
  const basis = String(meta.calculationBasis || meta.basis || meta.earnBasis || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return basis === "recurrent" || basis === "recurring" || basis === "monthlyvariable";
}

function displayArrearsName(name, treatment) {
  if (isLegacyLopReversal(name)) {
    if (treatment?.mode === "separate_components") return "Arr. LOP Reversal";
    return treatment?.mergedComponentName || "Arrears";
  }
  if (/salary\s*increment\s*arrear/i.test(name) && treatment?.mode === "merge_single") {
    return treatment?.mergedComponentName || "Arrears";
  }
  return name;
}

function buildPayslipModel(row, enriched) {
  const b = row?.breakdownJson || row?.breakdown || {};
  const lines = Array.isArray(b.componentLines || b.components)
    ? b.componentLines || b.components
    : [];
  const treatment = b.arrearsTreatment || { mode: "merge_single", mergedComponentName: "Arrears" };

  const payHeads = []; // { name, fixedSalary, monthlyEarnings }
  const deductions = [];
  const benefits = [];
  const seenEarn = new Map();
  const seenDed = new Map();

  const pushEarn = (name, fixedSalary, monthlyEarnings) => {
    const key = String(name || "").trim().toLowerCase();
    if (!key) return;
    const prev = seenEarn.get(key);
    if (prev) {
      prev.fixedSalary = round2(prev.fixedSalary + (Number(fixedSalary) || 0));
      prev.monthlyEarnings = round2(prev.monthlyEarnings + (Number(monthlyEarnings) || 0));
      return;
    }
    const rowOut = {
      name,
      fixedSalary: round2(Number(fixedSalary) || 0),
      monthlyEarnings: round2(Number(monthlyEarnings) || 0),
    };
    seenEarn.set(key, rowOut);
    payHeads.push(rowOut);
  };

  const pushDed = (name, amount) => {
    const key = String(name || "").trim().toLowerCase();
    if (!key) return;
    const abs = Math.abs(Number(amount) || 0);
    if (abs < 0.005) return;
    const prev = seenDed.get(key);
    if (prev) {
      prev.amount = round2(prev.amount + abs);
      return;
    }
    const rowOut = { name, amount: abs };
    seenDed.set(key, rowOut);
    deductions.push(rowOut);
  };

  if (lines.length) {
    lines.forEach((c) => {
      let name = String(c.name || c.componentName || "").trim();
      if (!name || /^lop\s*deduction$/i.test(name)) return;
      const amt = Number(c.amount ?? 0) || 0;
      const monthly = Number(c.monthlyAmount ?? c.monthly ?? 0) || 0;

      if (EMPLOYER_STATUTORY_RE.test(name) && !/employee/i.test(name)) {
        benefits.push({ name, amount: Math.abs(amt || monthly) });
        return;
      }
      if (isNonPayrollBenefit(name, c)) {
        benefits.push({ name, amount: Math.abs(amt || monthly) });
        return;
      }
      if (isEmployeeDeduction(name, amt, c)) {
        // Normalize Arr.PF / Arr.ESI labels
        if (/^arr\.?\s*pf\b/i.test(name)) name = "Arr.PF";
        if (/^arr\.?\s*esi\b/i.test(name)) name = "Arr.ESI";
        pushDed(name, amt);
        return;
      }

      name = displayArrearsName(name, treatment);
      const isArrearsOrVar =
        /arrears?/i.test(name) ||
        /^arr\./i.test(name) ||
        isLegacyLopReversal(c.name) ||
        /^leave\s*encashment$/i.test(name) ||
        isMonthlyVariableMeta(c);
      if (isArrearsOrVar) {
        // Monthly earnings only — not a fixed structure head
        pushEarn(name, 0, Math.abs(amt));
      } else if (c.meta?.midMonthProRata) {
        // Mid-month: Fixed always tip monthlyAmount (may be 0); earnings = day-blend
        pushEarn(name, monthly, Math.abs(amt));
      } else {
        // Fixed head: Fixed Salary = full month; Monthly Earnings = earned this month
        const fixedSal = monthly > 0 ? monthly : Math.abs(amt);
        pushEarn(name, fixedSal, Math.abs(amt));
      }
    });
  } else if (enriched?.amountsByName) {
    const calendarDays = Number(b.calendarDays) || 30;
    const paidDays = Number(b.paidDays ?? calendarDays);
    const factor = calendarDays > 0 ? paidDays / calendarDays : 1;
    Object.entries(enriched.amountsByName).forEach(([name, full]) => {
      const fixed = round2(Number(full) || 0);
      pushEarn(name, fixed, round2(fixed * factor));
    });
  }

  // Arr.PF / Arr.ESI from meta, or estimate for legacy breakdowns missing arrPfMeta
  let arrPf = Number(b.arrPfMeta?.arrPf) || 0;
  let arrEsi = Number(b.arrPfMeta?.arrEsi) || 0;
  if (!(arrPf > 0) && !seenDed.has("arr.pf")) {
    const pool = round2(
      (Number(b.lopReversalAmount) || 0) + (Number(b.revisionArrears) || 0)
    );
    const pfWage = Number(b.pfMeta?.actualPfWage ?? b.pfMeta?.pfEligibleWage) || 0;
    const earnSum = payHeads.reduce((s, p) => s + (Number(p.monthlyEarnings) || 0), 0);
    const grossBefore = Math.max(0, earnSum - pool);
    if (pool > 0 && pfWage > 0) {
      const ratio = grossBefore > 0 ? Math.min(1, pfWage / grossBefore) : 1;
      const rate = (Number(b.pfMeta?.employeeRate) || 12) / 100;
      arrPf = round2(pool * ratio * rate);
    }
  }
  if (arrPf > 0 && !seenDed.has("arr.pf")) pushDed("Arr.PF", arrPf);
  if (arrEsi > 0 && !seenDed.has("arr.esi")) pushDed("Arr.ESI", arrEsi);

  // Ensure Arrears pay-head when only lopReversalAmount exists
  if (
    Number(b.lopReversalAmount) > 0 &&
    !payHeads.some((p) => /arrears|lop\s*reversal/i.test(p.name))
  ) {
    pushEarn(
      treatment.mergedComponentName || "Arrears",
      0,
      Number(b.lopReversalAmount) || 0
    );
  }

  const fixedTotal = sumField(payHeads, "fixedSalary");
  const earningsTotal = sumField(payHeads, "monthlyEarnings");
  const deductionsTotal = sumField(deductions, "amount");
  const benefitsTotal = sumField(benefits, "amount");
  const computedNet = round2(earningsTotal - deductionsTotal);

  return {
    b,
    treatment,
    payHeads,
    deductions,
    benefits,
    fixedTotal,
    earningsTotal,
    deductionsTotal,
    benefitsTotal,
    computedNet,
    arrPf,
    arrEsi,
  };
}

function statutoryEmployerOnly(b, benefits) {
  const rows = [];
  const pf = b.pfMeta || {};
  if (pf.actualPfWage != null || pf.pfEligibleWage != null) {
    rows.push({
      label: "PF eligible wage",
      value: formatInr(pf.pfEligibleWage ?? pf.actualPfWage),
    });
  }
  if (pf.finalPfWage != null || pf.eligiblePfWage != null) {
    rows.push({
      label: "Final PF wage",
      value: formatInr(pf.finalPfWage ?? pf.eligiblePfWage),
    });
  }
  if (Number(pf.eps) > 0) rows.push({ label: "EPS (Pension)", value: formatInr(pf.eps) });
  if (Number(pf.epf) > 0) rows.push({ label: "EPF (Employer)", value: formatInr(pf.epf) });
  if (Number(pf.adminCharges) > 0) {
    rows.push({ label: "Admin charges", value: formatInr(pf.adminCharges) });
  }
  if (Number(pf.edli) > 0) rows.push({ label: "EDLI", value: formatInr(pf.edli) });
  if (Number(pf.employer) > 0) {
    rows.push({ label: "Total PF (Employer)", value: formatInr(pf.employer) });
  }
  const esiEr = Number(b.esiMeta?.employer);
  if (esiEr > 0) rows.push({ label: "ESI (Employer)", value: formatInr(esiEr) });
  if (Number(b.arrPfMeta?.arrPfWage) > 0) {
    rows.push({ label: "Arr. PF wage", value: formatInr(b.arrPfMeta.arrPfWage) });
  }
  // Benefits already listed — only add if missing from benefits table
  const has = (re) => benefits.some((x) => re.test(x.name));
  if (!has(/gratuity/i) && Number(b.structureAddonsMeta?.gratuity) > 0) {
    rows.push({ label: "Gratuity", value: formatInr(b.structureAddonsMeta.gratuity) });
  }
  return rows;
}

export default function PayrollBreakdownModal({
  darkMode,
  row,
  onClose,
  employee = null,
  salaryStructures = null,
  statutoryConfigJson = null,
  branchesList = null,
  statutoryOverridesByEmployeeId = null,
}) {
  const enriched = useMemo(() => {
    if (!employee || !salaryStructures?.length) return null;
    const payMonth = String(
      row?.breakdownJson?.payMonth || row?.breakdown?.payMonth || ""
    ).slice(0, 7);
    const [y, m] = payMonth.split("-").map(Number);
    const map = enrichEmployeesWithSalaryBreakdown({
      employees: [employee],
      structures: salaryStructures,
      statutoryConfigJson,
      branchesList: branchesList || [],
      selectedYear: y || new Date().getFullYear(),
      selectedMonth: (m || 1) - 1,
      statutoryOverridesByEmployeeId,
    });
    return map.get(Number(employee.id)) || map.get(String(employee.id)) || null;
  }, [
    employee,
    salaryStructures,
    statutoryConfigJson,
    branchesList,
    row,
    statutoryOverridesByEmployeeId,
  ]);

  const model = useMemo(() => buildPayslipModel(row, enriched), [row, enriched]);

  if (!row || typeof document === "undefined") return null;

  const {
    b,
    payHeads,
    deductions,
    benefits,
    fixedTotal,
    earningsTotal,
    deductionsTotal,
    benefitsTotal,
    computedNet,
  } = model;

  const storedGross = Number(b.grossPay);
  const storedDed = Number(b.totalDeductions);
  const storedNet = Number(b.netPay);
  const grossDisplay = Number.isFinite(storedGross) ? round2(storedGross) : earningsTotal;
  const dedDisplay = Number.isFinite(storedDed) ? round2(storedDed) : deductionsTotal;
  const netDisplay = Number.isFinite(storedNet) ? round2(storedNet) : computedNet;
  const matched =
    Math.abs(round2(earningsTotal - deductionsTotal) - computedNet) < 0.05 &&
    Math.abs(computedNet - netDisplay) < 1;

  const statutoryRows = statutoryEmployerOnly(b, benefits);
  const fnf = row.fnfSettlement || null;
  const border = darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]";
  const mutedBg = darkMode ? "bg-[hsl(var(--ds-muted))]/40" : "bg-[hsl(var(--ds-muted))]/45";

  return createPortal(
    <div
      className="fixed inset-0 z-[100060] flex items-center justify-center bg-black/50 p-3 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[min(94vh,960px)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${
          darkMode
            ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={`flex shrink-0 items-start justify-between border-b px-5 py-4 ${border}`}>
          <div>
            <h3 className="text-base font-semibold tracking-tight sm:text-lg">
              {b.empCode || row.employeeId} — {b.name || b.employeeName || ""}
            </h3>
            <p className="mt-0.5 text-sm text-[hsl(var(--ds-muted-fg))]">
              {b.payMonth || ""}
              {b.structureName || enriched?.structureName
                ? ` · ${b.structureName || enriched.structureName}`
                : ""}
            </p>
            {b.salaryRevisionEffectiveDate ||
            (b.structureEffectiveFrom &&
              Number(String(b.structureEffectiveFrom).slice(8, 10)) > 1) ? (
              <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                Fixed salary uses the revised structure for this pay month
                {b.salaryRevisionEffectiveDate
                  ? ` (revision effective ${String(b.salaryRevisionEffectiveDate).slice(0, 10)})`
                  : ` (structure effective ${String(b.structureEffectiveFrom).slice(0, 10)})`}
                . Monthly amounts day-blend pre/post revision days where mid-month applies.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
          {/* Quick facts */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <FactCard
              label="Annual CTC"
              value={formatInr(b.annualCtc)}
              sub={
                b.monthlyGross != null ? `Monthly ${formatInr(b.monthlyGross)}` : null
              }
            />
            <FactCard label="Gross" value={formatInr(grossDisplay)} />
            <FactCard label="Deductions" value={formatInr(dedDisplay)} tone="danger" />
            <FactCard label="Net pay" value={formatInr(netDisplay)} tone="primary" />
            <FactCard
              label="Paid / LOP"
              value={`${b.paidDays ?? "—"} / ${b.lopDays ?? 0}`}
              sub={b.calendarDays != null ? `${b.calendarDays} calendar days` : null}
            />
            <FactCard
              label="Present / LOP rev. days"
              value={`${b.presentDays ?? "—"} / ${b.lopReversalDays ?? 0}`}
              sub={
                Number(b.lopReversalAmount) > 0
                  ? `Rev. amt ${formatInr(b.lopReversalAmount)}`
                  : null
              }
            />
          </div>

          {/* Pay Heads */}
          <SectionCard title="Pay Heads" darkMode={darkMode} border={border} mutedBg={mutedBg}>
            {!payHeads.length ? (
              <p className="px-4 py-3 text-[hsl(var(--ds-muted-fg))]">No pay heads.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className={mutedBg}>
                  <tr>
                    <th className="w-10 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      #
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Component
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Fixed Salary
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Monthly Earnings
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payHeads.map((r, i) => (
                    <tr key={`${r.name}-${i}`} className={`border-t ${border}`}>
                      <td className="px-3 py-2 tabular-nums text-[hsl(var(--ds-muted-fg))]">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r.fixedSalary > 0 ? formatInr(r.fixedSalary) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">
                        {formatInr(r.monthlyEarnings)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`border-t ${border} ${mutedBg}`}>
                    <td colSpan={2} className="px-3 py-2.5 text-right font-semibold">
                      Total
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                      {formatInr(fixedTotal)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold">
                      {formatInr(earningsTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </SectionCard>

          {/* Deductions — once, includes Arr.PF / Arr.ESI */}
          <SectionCard
            title="Deductions"
            subtitle="PF, ESI, PT, TDS, Arr.PF, Arr.ESI and other salary deductions"
            darkMode={darkMode}
            border={border}
            mutedBg={mutedBg}
          >
            {!deductions.length ? (
              <p className="px-4 py-3 text-[hsl(var(--ds-muted-fg))]">No deductions.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className={mutedBg}>
                  <tr>
                    <th className="w-10 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      #
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Component
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.map((r, i) => (
                    <tr key={`${r.name}-${i}`} className={`border-t ${border}`}>
                      <td className="px-3 py-2 tabular-nums text-[hsl(var(--ds-muted-fg))]">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-[hsl(var(--ds-danger))]">
                        {formatInr(r.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`border-t ${border} ${mutedBg}`}>
                    <td colSpan={2} className="px-3 py-2.5 text-right font-semibold">
                      Total deductions
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold text-[hsl(var(--ds-danger))]">
                      {formatInr(deductionsTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </SectionCard>

          {/* Net — single reconciliation strip */}
          <div className={`rounded-xl border px-4 py-3 ${border} ${mutedBg}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Net pay
              </p>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  matched
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    : "bg-amber-500/15 text-amber-800 dark:text-amber-200"
                }`}
              >
                {matched ? "Matched" : "Review"}
              </span>
            </div>
            <p className="mt-2 text-base font-semibold tabular-nums sm:text-lg">
              {formatInr(earningsTotal)}{" "}
              <span className="font-normal text-[hsl(var(--ds-muted-fg))]">−</span>{" "}
              {formatInr(deductionsTotal)}{" "}
              <span className="font-normal text-[hsl(var(--ds-muted-fg))]">=</span>{" "}
              <span className="text-[hsl(var(--ds-primary))]">{formatInr(computedNet)}</span>
            </p>
            <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
              Monthly earnings − deductions = net salary bank credit
              {Number.isFinite(storedNet) ? ` · Stored ${formatInr(storedNet)}` : ""}
            </p>
          </div>

          {/* Employer benefits — once */}
          <SectionCard
            title="Employer benefits & contributions"
            subtitle="Counted in CTC — not in Gross or Net bank credit"
            darkMode={darkMode}
            border={border}
            mutedBg={mutedBg}
          >
            {!benefits.length ? (
              <p className="px-4 py-3 text-[hsl(var(--ds-muted-fg))]">None this month.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className={mutedBg}>
                  <tr>
                    <th className="w-10 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      #
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Component
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {benefits.map((r, i) => (
                    <tr key={`${r.name}-${i}`} className={`border-t ${border}`}>
                      <td className="px-3 py-2 tabular-nums text-[hsl(var(--ds-muted-fg))]">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">
                        {formatInr(r.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`border-t ${border} ${mutedBg}`}>
                    <td colSpan={2} className="px-3 py-2.5 text-right font-semibold">
                      Total employer cost
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold">
                      {formatInr(benefitsTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </SectionCard>

          {/* Statutory — employer / wage bases only (no repeat of employee deductions) */}
          {statutoryRows.length ? (
            <SectionCard
              title="Statutory summary"
              subtitle="PF wage bases and employer split — employee PF/ESI/PT/TDS/Arr.PF are listed under Deductions"
              darkMode={darkMode}
              border={border}
              mutedBg={mutedBg}
            >
              <table className="w-full text-sm">
                <tbody>
                  {statutoryRows.map((r) => (
                    <tr key={r.label} className={`border-t first:border-t-0 ${border}`}>
                      <td className="px-3 py-2 font-medium">{r.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">
                        {r.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          ) : null}

          {fnf ? (
            <SectionCard
              title="Full & Final"
              subtitle={fnf.status ? `Status: ${fnf.status}` : null}
              darkMode={darkMode}
              border={border}
              mutedBg={mutedBg}
            >
              <p className="px-4 py-3 text-base font-semibold tabular-nums">
                Net payable {formatInr(fnf.netAmount)}
              </p>
            </SectionCard>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function FactCard({ label, value, sub, tone = "neutral" }) {
  const toneCls =
    tone === "primary"
      ? "text-[hsl(var(--ds-primary))]"
      : tone === "danger"
        ? "text-[hsl(var(--ds-danger))]"
        : "text-[hsl(var(--ds-fg))]";
  return (
    <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
        {label}
      </p>
      <p className={`mt-1 text-base font-bold tabular-nums sm:text-[17px] ${toneCls}`}>
        {value ?? "—"}
      </p>
      {sub ? (
        <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">{sub}</p>
      ) : null}
    </div>
  );
}

function SectionCard({ title, subtitle, children, border, mutedBg }) {
  return (
    <div className={`overflow-hidden rounded-xl border ${border}`}>
      <div className={`border-b px-4 py-2.5 ${border} ${mutedBg}`}>
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">{subtitle}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}
